import { EmberPlayMode, EntityId } from "game-common/models"
import { Container, Graphics, Rectangle, Text } from "pixi.js"
import { TextRenderer } from "../text/text_renderer"

// A mode belongs to this invitation. Selection is local until the explicit Invite press.
export class EmberInviteModal extends Container {
    private panel = new Container()
    private scrim = new Graphics()
    private title: Text
    private options = new Map<EmberPlayMode, Graphics>()
    private target?: EntityId
    private mode: EmberPlayMode = "pvp"

    constructor(private onInvite: (id: EntityId, mode: EmberPlayMode) => void) {
        super()
        this.addChild(this.scrim, this.panel)
        this.interactive = true
        for (const event of ["pointermove", "pointerup", "rightclick"]) {
            this.on(event, (e: any) => e.stopPropagation())
        }
        this.on("contextmenu", (e: any) => { e.preventDefault(); e.stopPropagation() })
        this.scrim.interactive = true
        this.scrim.on("pointerdown", (e: any) => { e.stopPropagation(); this.close() })
        const plate = new Graphics()
        plate.beginFill(0x1b1512, 0.98).lineStyle(2, 0xf2a94e, 0.9).drawRoundedRect(0, 0, 310, 230, 10).endFill()
        this.panel.addChild(plate)
        this.panel.interactive = true
        this.panel.hitArea = new Rectangle(0, 0, 310, 230)
        this.panel.on("pointerdown", (e: any) => e.stopPropagation())
        this.title = this.label("", 14, 14, 15)
        this.label("Choose this player's play mode", 14, 42, 12)
        ;(["pvp", "cooperation"] as EmberPlayMode[]).forEach((mode, i) => {
            const card = new Graphics()
            card.position.set(14 + i * 146, 68)
            card.interactive = true
            card.cursor = "pointer"
            card.hitArea = new Rectangle(0, 0, 136, 100)
            const title = new TextRenderer().fontSize(14).color(0xfff3e0).bold().render(mode === "pvp" ? "PVP" : "Cooperation")
            title.position.set(10, 12)
            const desc = new TextRenderer().fontSize(11).color(0xfff3e0).render(mode === "pvp" ? "No restrictions\nPlayer hits allowed" : "Work together\nNo player hits")
            desc.position.set(10, 40)
            card.addChild(title, desc)
            card.on("pointerdown", (e: any) => { e.stopPropagation(); this.selectMode(mode) })
            this.options.set(mode, card)
            this.panel.addChild(card)
        })
        this.button("Cancel", 14, () => this.close())
        this.button("Invite", 160, () => this.confirm())
        this.visible = false
    }

    private label(text: string, x: number, y: number, size: number): Text {
        const label = new TextRenderer().fontSize(size).color(0xfff3e0).render(text) as Text
        label.position.set(x, y)
        this.panel.addChild(label)
        return label
    }

    private button(text: string, x: number, action: () => void) {
        const button = new Graphics()
        const affirmative = text === "Invite"
        const rim = affirmative ? 0x7be08a : 0x8d97ad
        const draw = (hover: boolean) => {
            button.clear()
            button.lineStyle(1, rim, hover ? 1 : 0.75)
            button.beginFill(hover ? 0x33405e : 0x1b2030, 0.95)
            button.drawRoundedRect(0, 0, 136, 32, 4)
            button.endFill()
        }
        draw(false)
        button.on("mouseover", () => draw(true))
        button.on("mouseout", () => draw(false))
        button.position.set(x, 184)
        button.interactive = true
        button.cursor = "pointer"
        button.hitArea = new Rectangle(0, 0, 136, 32)
        const label = new TextRenderer().fontSize(13).color(affirmative ? 0xdcffe2 : 0xd4d8e0).render(text)
        label.position.set((136 - label.width) / 2, 8)
        button.addChild(label)
        button.on("pointerdown", (e: any) => { e.stopPropagation(); action() })
        this.panel.addChild(button)
    }

    resize(width: number, height: number, left = 0, top = 0) {
        this.scrim.clear().beginFill(0x000000, 0.4).drawRect(left, top, width, height).endFill()
        this.scrim.hitArea = new Rectangle(left, top, width, height)
        const scale = Math.max(0, Math.min(1, (width - 16) / 310, (height - 16) / 230))
        this.panel.scale.set(scale)
        this.panel.position.set(left + (width - 310 * scale) / 2, top + (height - 230 * scale) / 2)
    }

    open(id: EntityId, name?: string) {
        this.target = id
        this.title.text = `Invite ${(name || id).slice(0, 23)}`
        this.selectMode("pvp")
        this.visible = true
    }

    selectMode(mode: EmberPlayMode) {
        this.mode = mode
        this.options.forEach((card, option) => {
            const selected = option === mode
            card.clear().beginFill(selected ? 0x33402e : 0x2a201a)
                .lineStyle(selected ? 2 : 1, selected ? 0x7be08a : 0x6b4f38)
                .drawRoundedRect(0, 0, 136, 100, 8).endFill()
        })
    }

    confirm() {
        if (!this.target) return
        const id = this.target
        const mode = this.mode
        this.close()
        this.onInvite(id, mode)
    }

    close() { this.target = undefined; this.visible = false }
    isOpen() { return !!this.target }
    getTargetEntityId() { return this.target }
}
